import React from 'react';
import { Link } from 'react-router-dom';
import PublicPageNav from '../components/PublicPageNav';

export default function TermsOfService() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#2D2D2D', color: '#e5e5e5' }} data-testid="terms-of-service-page">
      <PublicPageNav testIdPrefix="terms" />

      <main className="max-w-3xl mx-auto px-6 py-12">
        <p className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: '#C9A962' }}>Legal</p>
        <h1 className="text-4xl sm:text-5xl font-bold mb-3 text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>
          Terms of <span style={{ color: '#C9A962' }}>Service</span>
        </h1>
        <p className="text-sm mb-10" style={{ color: '#a0a0a0' }}>Last updated: February 11, 2026</p>

        <Section title="1. Acceptance">
          <p>
            These Terms of Service ("Terms") form a binding agreement between you and Noble Empire Cleaning Services, LLC ("Noble Empire", "NECS", "we", "us"). By creating an account or using the NECS Resident App, NECS Staff App, or noble­empirecs.com (the "Services"), you agree to be bound by these Terms and our <Link to="/privacy" className="text-[#C9A962] hover:underline">Privacy Policy</Link>.
          </p>
        </Section>

        <Section title="2. Eligibility">
          <p>You must be at least 18 years old (or the age of majority in your jurisdiction) to create an account. By signing up, you represent that the information you provide is accurate and that you are authorized to bind any organization on whose behalf you act.</p>
        </Section>

        <Section title="3. Account Roles">
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Resident / Tenant:</strong> Receives valet trash service and may submit requests, complaints, and feedback.</li>
            <li><strong>Property Manager:</strong> Manages one or more properties and reviews service reports tied to those properties.</li>
            <li><strong>Staff (Supervisor, Crew Leader, Agent):</strong> Performs scheduled service on behalf of Noble Empire and is subject to additional employment or contractor agreements.</li>
            <li><strong>Company Admin:</strong> Internal Noble Empire administrator.</li>
          </ul>
        </Section>

        <Section title="4. The Service">
          <p>
            Noble Empire provides valet trash pickup, bulk removal, dumpster sanitation, power washing, and common-area cleaning to multifamily properties in the Dallas–Fort Worth area. Service schedules are set per-property and are governed by the service agreement between Noble Empire and the Property Manager — not by individual tenants.
          </p>
          <p>
            Trash must be placed outside the resident's unit between 5:00 PM and 7:30 PM local time on scheduled service nights. Items placed before 5:00 PM may result in a violation report. Items must be in a sealed bag in an enclosed trash container weighing no more than 25 lbs.
          </p>
        </Section>

        <Section title="5. Acceptable Use">
          <p>You agree not to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Misrepresent your identity, role, or property assignment.</li>
            <li>Upload hazardous, defamatory, or unlawful content (including photos).</li>
            <li>Attempt to reverse engineer, scrape, or circumvent security of the Services.</li>
            <li>Use the Services to harass any tenant, employee, or contractor.</li>
            <li>Place hazardous materials (paint, batteries, chemicals, electronics) in valet trash.</li>
          </ul>
        </Section>

        <Section title="6. Violations and Fees">
          <p>
            Violations of placement rules may result in (a) a documented violation report shared with your Property Manager, (b) a fee passed through your lease or property agreement, and/or (c) suspension of valet trash privileges. Fee schedules are set by the Property Manager in coordination with Noble Empire.
          </p>
        </Section>

        <Section title="7. Mobile App Specific Terms">
          <p>
            If you download the NECS Resident App or NECS Staff App from the Apple App Store or Google Play Store, the additional terms of those platforms apply. The apps require location, camera, and notification permissions for full functionality. You may revoke any permission in your device settings.
          </p>
        </Section>

        <Section title="8. SMS / Text Messaging">
          <p>
            By providing your mobile phone number to Noble Empire Cleaning Services and
            enabling SMS in your notification preferences, you agree to receive text
            messages from us. SMS is used for service alerts, one-time verification codes
            (OTP), and — when authorized by an Administrator — emergency blackout notices.
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li data-testid="sms-terms-no-share">
              <strong>We will not share or sell your mobile phone number or SMS opt-in
              information with any third parties for their marketing purposes.</strong>
            </li>
            <li data-testid="sms-terms-frequency">
              <strong>Message frequency varies</strong> based on your service activity
              and notification preferences.
            </li>
            <li data-testid="sms-terms-rates">
              <strong>Message and data rates may apply.</strong>
            </li>
            <li data-testid="sms-terms-stop-help">
              <strong>Reply STOP to cancel SMS notifications at any time. Reply HELP for
              assistance.</strong>
            </li>
          </ul>
          <p>
            SMS is delivered via Twilio in the United States and Canada. You can change
            your delivery channel at any time in <em>Profile → Notification Preferences</em>
            (Email is the default; SMS is opt-in).
          </p>
        </Section>

        <Section title="9. Intellectual Property">
          <p>
            The NECS name, NECS crown-and-laurel emblem, app icons, software, and all related content are owned by Noble Empire Cleaning Services, LLC and protected by U.S. copyright and trademark law. You receive a limited, revocable, non-exclusive license to use the Services for their intended purpose.
          </p>
        </Section>

        <Section title="10. User Content">
          <p>
            You retain ownership of photos, notes, and feedback you submit ("User Content"). By submitting User Content, you grant Noble Empire a worldwide, royalty-free license to use, display, and process that content for the purpose of operating the Services, fulfilling our service agreements with Property Managers, and complying with legal obligations.
          </p>
        </Section>

        <Section title="11. Disclaimers">
          <p>
            THE SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND. NOBLE EMPIRE DOES NOT WARRANT UNINTERRUPTED, ERROR-FREE OPERATION. WEATHER, TRAFFIC, OR FORCE-MAJEURE EVENTS MAY DELAY SERVICE. AI-GENERATED SUGGESTIONS (E.G. AUTO-VIOLATION CATEGORIES) ARE NON-BINDING AND SUBJECT TO HUMAN REVIEW.
          </p>
        </Section>

        <Section title="12. Limitation of Liability">
          <p>
            TO THE FULLEST EXTENT PERMITTED BY LAW, NOBLE EMPIRE WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF DATA, PROFITS, OR GOODWILL ARISING OUT OF OR IN CONNECTION WITH THE SERVICES. OUR TOTAL LIABILITY WILL NOT EXCEED THE FEES PAID TO US IN THE 12 MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM, OR USD $100, WHICHEVER IS GREATER.
          </p>
        </Section>

        <Section title="13. Termination">
          <p>
            We may suspend or terminate your access to the Services at any time, with or without cause, including for violation of these Terms. You may delete your account at any time by emailing <a className="text-[#C9A962] hover:underline" href="mailto:support@nobleempirecs.com">support@nobleempirecs.com</a>.
          </p>
        </Section>

        <Section title="14. Governing Law">
          <p>These Terms are governed by the laws of the State of Texas, without regard to its conflict-of-laws principles. Exclusive venue lies in the state or federal courts located in Dallas County, Texas.</p>
        </Section>

        <Section title="15. Device Permissions & Field Staff Responsibility" data-testid="terms-device-permissions">
          <p data-testid="terms-device-permissions-clause">
            Field staff — drivers, supervisors, crew leaders, and site agents — are
            responsible for ensuring that all device permissions required by the
            NECS Staff App are enabled on their smartphone or tablet at all times
            while on shift. This includes, at minimum:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Precise Location (including background / "Allow all the time")</strong> — required for live route tracking, proximity-based dispatch, and property arrival verification.</li>
            <li><strong>Camera</strong> — required for QR scan check-ins, violation photos, and vehicle safety-check documentation.</li>
            <li><strong>Notifications</strong> — required to receive dispatch alerts, emergency broadcasts, and shift-change updates.</li>
            <li><strong>Microphone</strong> (if voice-note reporting is used) — required to record spoken violation notes.</li>
          </ul>
          <p>
            Denying, revoking, or later disabling any of the required permissions
            will impair or disable core Staff App functionality. Staff who cannot or
            will not enable the required permissions are responsible for notifying
            their supervisor immediately so an alternate reporting workflow can be
            put in place. Persistent operation without the required permissions may
            be treated as a policy violation under Section 6.
          </p>
        </Section>

        <Section title="16. Acceptable Use — Anti-Harassment & Anti-Bullying" data-testid="terms-anti-harassment">
          <p data-testid="terms-anti-harassment-clause">
            All communications sent through the NECS platform — including in-app
            messages, tenant-directed notifications, broadcast texts, violation
            comments, dispute responses, and any user-generated content — must be
            professional and respectful. You agree that you will <strong>not</strong>
            {' '}use the Services to:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Harass, bully, intimidate, threaten, dox, or stalk any other user, tenant, property manager, staff member, or third party.</li>
            <li>Post, send, or transmit content that is hateful, discriminatory, defamatory, sexually explicit, or that targets a person based on race, ethnicity, national origin, religion, gender, gender identity, sexual orientation, age, disability, or any other protected characteristic.</li>
            <li>Post, send, or transmit content that promotes violence, self-harm, or illegal activity.</li>
            <li>Impersonate another person or misrepresent your affiliation with NECS or any tenant/property.</li>
            <li>Repeatedly contact another user after they have asked you to stop, or use the platform to circumvent a block or restriction.</li>
          </ul>
          <p>
            NECS operates a zero-tolerance policy for harassment and bullying.
            Reports may be submitted from any message or content by tapping "Report"
            in-app or by emailing{' '}
            <a className="text-[#C9A962] hover:underline" href="mailto:support@nobleempirecs.com">support@nobleempirecs.com</a>.
            Verified violations may result in content removal, suspension, permanent
            account termination, and — where warranted — referral to law enforcement.
          </p>
        </Section>

        <Section title="17. Changes">
          <p>
            We may revise these Terms from time to time. Material changes will be posted here with a new "Last updated" date and, where required, communicated by email or in-app notice. Continued use after a change constitutes acceptance.
          </p>
        </Section>

        <Section title="18. Contact">
          <p>
            Noble Empire Cleaning Services, LLC<br />
            2450 Lakeside Pkwy, Ste 150<br />
            Flower Mound, TX 75022<br />
            <a className="text-[#C9A962] hover:underline" href="mailto:support@nobleempirecs.com">support@nobleempirecs.com</a> · <a className="text-[#C9A962] hover:underline" href="tel:+18882413077">+1 (888) 241-3077</a>
          </p>
        </Section>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-10" data-testid={`section-${title.split(' ')[0].toLowerCase().replace(/[^a-z0-9]/g, '')}`}>
      <h2 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed" style={{ color: '#d6d6d6' }}>{children}</div>
    </section>
  );
}
